import { useMutation } from "@apollo/client";
import { showNotification, updateNotification } from "@mantine/notifications";
import { FC, Fragment } from "react";
import { SubmitHandler, useForm, DefaultValues } from "react-hook-form";
import { useNavigate, useSearch } from "react-location";
import { useTranslation } from "react-i18next";
import { resetPassword } from "../../graphql/mutations/auth";
import { usePage } from "../../hooks/usePage";

interface ResetPasswordForm {
    email: string;
    otp: string;
    newPassword: string;
    confirmPassword: string;
}

const ResetPasswordScreen: FC = () => {
    const { t } = useTranslation();
    usePage(`Kava | ${t("reset_password_page.title")}`);
    const navigate = useNavigate();
    const search = useSearch();

    const {
        register,
        formState: { errors, isValid },
        handleSubmit,
        watch,
    } = useForm<ResetPasswordForm>({
        defaultValues: {
            email: (search as { email?: string }).email || "",
        } as DefaultValues<ResetPasswordForm>,
        mode: "onChange",
    });

    const newPassword = watch("newPassword", "");

    const [executePasswordReset, { loading }] = useMutation(resetPassword);

    const onSubmit: SubmitHandler<ResetPasswordForm> = (data) => {
        if (data.newPassword !== data.confirmPassword) {
            showNotification({
                id: "mismatch",
                title: t(
                    "reset_password_page.notifications.validation_error_title",
                ),
                message: t(
                    "reset_password_page.notifications.passwords_must_match",
                ),
                color: "red",
            });
            return;
        }

        showNotification({
            id: "reset-password",
            loading: true,
            title: t(
                "reset_password_page.notifications.setting_password_title",
            ),
            message: t(
                "reset_password_page.notifications.setting_password_message",
            ),
            autoClose: false,
            disallowClose: true,
        });

        executePasswordReset({
            variables: {
                input: {
                    email: data.email,
                    token: data.otp,
                    password: data.newPassword,
                    password_confirmation: data.newPassword,
                },
            },
        })
            .then((res) => {
                const result = res.data?.resetPassword;
                if (result?.status) {
                    updateNotification({
                        id: "reset-password",
                        title: t(
                            "reset_password_page.notifications.success_title",
                        ),
                        message:
                            result.message ||
                            t(
                                "reset_password_page.notifications.success_message",
                            ),
                        color: "blue",
                    });
                    navigate({ to: "/cpanel-login", replace: true });
                } else {
                    updateNotification({
                        id: "reset-password",
                        title: t(
                            "reset_password_page.notifications.failed_title",
                        ),
                        message:
                            result?.message ||
                            t(
                                "reset_password_page.notifications.failed_message",
                            ),
                        color: "red",
                    });
                }
            })
            .catch((error) => {
                updateNotification({
                    id: "reset-password",
                    title: t("reset_password_page.notifications.error_title"),
                    message:
                        error.message ||
                        t("reset_password_page.notifications.error_message"),
                    color: "red",
                });
            });
    };

    return (
        <Fragment>
            <div className="flex flex-col items-center">
                <h2 className="mt-6 text-3xl font-medium text-gray-900">
                    {t("reset_password_page.header")}
                </h2>
                <p className="mt-2 text-sm text-gray-600">
                    {t("reset_password_page.description")}
                </p>
            </div>
            <div className="mt-8 max-w-sm w-full">
                <div className="mt-6">
                    <form
                        className="space-y-6"
                        onSubmit={handleSubmit(onSubmit)}>
                        <div>
                            <label
                                htmlFor="otp"
                                className="block text-sm font-medium text-gray-700">
                                {t("reset_password_page.otp_label")}
                            </label>
                            <div className="mt-1">
                                <input
                                    type="text"
                                    {...register("otp", {
                                        required: t(
                                            "reset_password_page.otp_required",
                                        ),
                                        minLength: {
                                            value: 4,
                                            message: t(
                                                "reset_password_page.otp_min_length",
                                            ),
                                        },
                                    })}
                                    id="otp"
                                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2 border"
                                    placeholder={t(
                                        "reset_password_page.otp_placeholder",
                                    )}
                                />
                                {errors?.otp && (
                                    <p className="text-red-400 text-sm mt-1">
                                        {errors?.otp?.message}
                                    </p>
                                )}
                            </div>
                        </div>
                        <div>
                            <label
                                htmlFor="newPassword"
                                className="block text-sm font-medium text-gray-700">
                                {t("reset_password_page.new_password_label")}
                            </label>
                            <div className="mt-1">
                                <input
                                    type="password"
                                    {...register("newPassword", {
                                        required: t(
                                            "reset_password_page.new_password_required",
                                        ),
                                        minLength: {
                                            value: 8,
                                            message: t(
                                                "reset_password_page.password_min_length",
                                            ),
                                        },
                                    })}
                                    id="newPassword"
                                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2 border"
                                    placeholder={t(
                                        "reset_password_page.new_password_placeholder",
                                    )}
                                />
                                {errors?.newPassword && (
                                    <p className="text-red-400 text-sm mt-1">
                                        {errors?.newPassword?.message}
                                    </p>
                                )}
                            </div>
                        </div>
                        <div>
                            <label
                                htmlFor="confirmPassword"
                                className="block text-sm font-medium text-gray-700">
                                {t(
                                    "reset_password_page.confirm_password_label",
                                )}
                            </label>
                            <div className="mt-1">
                                <input
                                    type="password"
                                    {...register("confirmPassword", {
                                        required: t(
                                            "reset_password_page.confirm_required",
                                        ),
                                        validate: (value) =>
                                            value === newPassword ||
                                            t(
                                                "reset_password_page.passwords_no_match",
                                            ),
                                    })}
                                    id="confirmPassword"
                                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2 border"
                                    placeholder={t(
                                        "reset_password_page.confirm_password_placeholder",
                                    )}
                                />
                                {errors?.confirmPassword && (
                                    <p className="text-red-400 text-sm mt-1">
                                        {errors?.confirmPassword?.message}
                                    </p>
                                )}
                            </div>
                        </div>

                        <div>
                            <button
                                type="submit"
                                disabled={loading || !isValid}
                                className="w-full flex justify-center py-2 px-4 border bg-white rounded-md shadow-sm text-sm font-medium text-green-600 hover:text-white border-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:bg-gray-300 disabled:border-gray-300 disabled:text-gray-500 transition duration-200">
                                {t("reset_password_page.submit_button")}
                                {loading && (
                                    <svg
                                        className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24">
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"></circle>
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </Fragment>
    );
};

export default ResetPasswordScreen;
